import { useState } from 'react';
import { User } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import PhotoLightboxComponent from './PhotoLightbox';

/**
 * UserAvatar - Consistent, clickable avatar for the entire app.
 * Clicking opens the user's photo gallery in a full-screen lightbox.
 *
 * Props:
 * - src: string - Avatar URL (relative or absolute)
 * - alt: string - Alt text
 * - size: 'xs'|'sm'|'md'|'lg'|'xl'|'2xl'|'3xl'
 * - className: string - Additional classes
 * - fallbackInitial: string - Char for fallback
 * - updatedAt: string - Timestamp for cache busting
 * - userId: string - Opens gallery lightbox on click when provided
 * - userName: string - Display name in the lightbox header
 * - clickable: bool - Force clickable even without userId (shows single photo)
 * - onAvatarChange: fn - Called when avatar is changed via lightbox
 */
export default function UserAvatar({
  src,
  alt = 'User',
  size = 'md',
  className = '',
  fallbackInitial,
  updatedAt,
  userId,
  userName,
  clickable = true,
  onAvatarChange,
}) {
  const [hasError, setHasError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-24 h-24 text-2xl',
    '3xl': 'w-32 h-32 text-3xl',
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
    '2xl': 'w-12 h-12',
    '3xl': 'w-16 h-16',
  };

  const getImageUrl = () => {
    if (!src) return null;
    if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) {
      if (updatedAt && !src.startsWith('data:')) {
        const sep = src.includes('?') ? '&' : '?';
        return `${src}${sep}v=${new Date(updatedAt).getTime()}`;
      }
      return src;
    }
    if (src.startsWith('/api')) {
      const full = `${API_BASE_URL}${src}`;
      return updatedAt ? `${full}?v=${new Date(updatedAt).getTime()}` : full;
    }
    const full = `${API_BASE_URL}${src.startsWith('/') ? '' : '/'}${src}`;
    return updatedAt ? `${full}?v=${new Date(updatedAt).getTime()}` : full;
  };

  const imageUrl = getImageUrl();
  const showImage = imageUrl && !hasError;
  const canClick = clickable && (userId || showImage);

  const handleClick = (e) => {
    // Stop propagation through both React and native DOM to prevent Link navigation
    e.stopPropagation();
    e.preventDefault();
    if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    if (canClick) setLightboxOpen(true);
  };

  return (
    <>
      <div
        role={canClick ? 'button' : undefined}
        tabIndex={canClick ? 0 : undefined}
        aria-label={canClick ? `View ${alt} photos` : undefined}
        onClick={canClick ? handleClick : undefined}
        onKeyDown={canClick ? (e) => { if (e.key === 'Enter') handleClick(e); } : undefined}
        className={`rounded-full overflow-hidden bg-primary/20 flex items-center justify-center flex-shrink-0 ${sizeClasses[size]} ${canClick ? 'cursor-pointer hover:ring-2 hover:ring-orange-500/40 transition-shadow' : ''} ${className}`}
        data-testid="user-avatar"
      >
        {showImage ? (
          <img src={imageUrl} alt={alt} className="w-full h-full object-cover" onError={() => setHasError(true)} />
        ) : fallbackInitial ? (
          <span className="font-bold text-primary uppercase">{fallbackInitial.charAt(0)}</span>
        ) : (
          <User className={`${iconSizes[size]} text-primary`} />
        )}
      </div>

      {lightboxOpen && (
        <PhotoLightboxComponent
          userId={userId}
          userName={userName || alt}
          initialPhotoUrl={src}
          onClose={() => setLightboxOpen(false)}
          onAvatarChange={(newUrl) => {
            if (onAvatarChange) onAvatarChange(newUrl);
          }}
        />
      )}
    </>
  );
}

// Convenience exports with userId passthrough

export function NavbarAvatar({ user }) {
  return (
    <UserAvatar
      src={user?.picture || user?.avatar_url}
      alt={user?.name || 'User'}
      size="sm"
      fallbackInitial={user?.name}
      updatedAt={user?.avatar_updated_at}
      clickable={false}
    />
  );
}

export function PostAuthorAvatar({ author }) {
  return (
    <UserAvatar
      src={author?.avatar || author?.avatar_url || author?.author_avatar}
      alt={author?.name || author?.author_name || 'Author'}
      size="md"
      fallbackInitial={author?.name || author?.author_name}
      updatedAt={author?.avatar_updated_at}
      userId={author?.user_id || author?.author_id}
      userName={author?.name || author?.author_name}
    />
  );
}

export function ProfileAvatar({ user, size = '2xl', onAvatarChange }) {
  return (
    <UserAvatar
      src={user?.picture || user?.avatar_url || user?.avatar}
      alt={user?.name || user?.stage_name || user?.display_name || 'Profile'}
      size={size}
      fallbackInitial={user?.name || user?.stage_name || user?.display_name}
      updatedAt={user?.avatar_updated_at}
      userId={user?.user_id}
      userName={user?.name || user?.stage_name || user?.display_name}
      onAvatarChange={onAvatarChange}
    />
  );
}

export function CommentAvatar({ author }) {
  return (
    <UserAvatar
      src={author?.avatar_url || author?.picture}
      alt={author?.name || 'User'}
      size="sm"
      fallbackInitial={author?.name}
      updatedAt={author?.avatar_updated_at}
      userId={author?.user_id}
      userName={author?.name}
    />
  );
}

export function ListAvatar({ user }) {
  return (
    <UserAvatar
      src={user?.avatar_url || user?.picture || user?.avatar}
      alt={user?.name || user?.stage_name || user?.display_name || 'User'}
      size="md"
      fallbackInitial={user?.name || user?.stage_name || user?.display_name}
      updatedAt={user?.avatar_updated_at}
      userId={user?.user_id}
      userName={user?.name || user?.stage_name || user?.display_name}
    />
  );
}

export function CreativeCardAvatar({ creative }) {
  return (
    <UserAvatar
      src={creative?.avatar_url || creative?.picture}
      alt={creative?.stage_name || creative?.display_name || creative?.name || 'Creative'}
      size="xl"
      fallbackInitial={creative?.stage_name || creative?.display_name || creative?.name}
      updatedAt={creative?.avatar_updated_at}
      userId={creative?.user_id}
      userName={creative?.stage_name || creative?.display_name || creative?.name}
    />
  );
}
